import { AnimatePresence } from "motion/react";
import type React from "react";
import { useRef, useState } from "react";

import { SearchSmIcon } from "@/components/Icons/SearchSmIcon";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { SidebarItemFloatingTooltip } from "@/components/Sidebar/SidebarItem";
import { useSidebarCollapsedTooltip } from "@/components/Sidebar/useSidebarCollapsedTooltip";
import { usePostHog } from "@/external/posthog-client";
import { mergeRefs } from "@/helpers/mergeRefs";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { useBool } from "@/hooks/useBool";
import { useKey } from "@/hooks/useKey";
import { useSidebarManager } from "@/hooks/useSidebar";
import { useTranslation } from "@/translations";
import { RuntimeFloatingPortal, useRuntimeFloatingPanel } from "@/ui-adapters/RuntimeFloatingPanel";

import { IconButton } from "../Button/IconButton";
import { PortfolioSidebarProjectSwitcher } from "./PortfolioSidebarProjectSwitcher";

export function PortfolioSidebarProjectsSection(): React.ReactNode {
  const { data: linkedProjects } = useLinkedProjects();
  const isSearchAvailable = (linkedProjects?.length ?? 0) >= 2;
  const [isProjectSwitcherVisible, projectSwitcherVisibilityHandlers] = useBool();
  const { isCollapsed } = useSidebarManager();
  const { t } = useTranslation();
  const postHog = usePostHog();
  const searchTriggerRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { referenceRef, panelProps } = useRuntimeFloatingPanel({
    placement: isCollapsed ? "right-start" : "bottom",
    open: isProjectSwitcherVisible,
  });
  const {
    showTooltip,
    hideTooltip,
    referenceRef: tooltipReferenceRef,
    panelProps: tooltipPanelProps,
    isTooltipVisible,
  } = useSidebarCollapsedTooltip();

  const closeProjectSwitcher = () => {
    projectSwitcherVisibilityHandlers.setFalse();
    setSearchQuery("");
    searchTriggerRef.current?.focus();
  };

  useKey("Escape", closeProjectSwitcher, isProjectSwitcherVisible);

  if (!isSearchAvailable) {
    return null;
  }

  return (
    <>
      <div className="relative flex w-full flex-col items-center justify-start">
        {isCollapsed && (
          <IconButton
            icon={<SearchSmIcon />}
            variant="soft"
            onClick={() => {
              projectSwitcherVisibilityHandlers.setTrue();
              postHog.capture("clicked_portfolio_sidebar_project_search");
            }}
            ref={mergeRefs(referenceRef, tooltipReferenceRef, searchTriggerRef)}
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
            title={t("component.project-switcher.search.placeholder")}
            withTooltip={false}
          />
        )}
        {!isCollapsed && (
          <SearchInput
            appearance="quiet"
            width="full"
            placeholder={t("component.project-switcher.search.placeholder")}
            value={searchQuery}
            name="portfolio-sidebar-project-search"
            aria-label={t("component.project-switcher.search.placeholder")}
            aria-controls="portfolio-sidebar-project-switcher"
            aria-expanded={isProjectSwitcherVisible}
            aria-haspopup="dialog"
            onChange={(event) => {
              setSearchQuery(event.target.value);
              projectSwitcherVisibilityHandlers.setTrue();
            }}
            onClick={projectSwitcherVisibilityHandlers.setTrue}
            ref={mergeRefs(referenceRef, searchTriggerRef)}
          />
        )}
      </div>

      <PortfolioSidebarProjectSwitcher
        isOpened={isProjectSwitcherVisible}
        onClose={closeProjectSwitcher}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        floating={panelProps}
      />
      <AnimatePresence>
        {isCollapsed && isTooltipVisible && (
          <RuntimeFloatingPortal>
            <SidebarItemFloatingTooltip
              label={t("component.project-switcher.search.placeholder")}
              floating={tooltipPanelProps}
              isVisible={isTooltipVisible}
            />
          </RuntimeFloatingPortal>
        )}
      </AnimatePresence>
    </>
  );
}
